/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import Retracked from 'js/lib/retracked';

import { Typography, typography2 } from '@coursera/cds-core';

import { getDefaultArticleSourceRenderNodes } from 'bundles/shared-articles/components/RichTextNodeRenderers';
import RichText, { getDefaultRichTextOptions } from 'bundles/unified-seo-content-common/components/rich-text/RichText';

import _t from 'i18n!nls/articles';

type Props = {
  body: Document;
};

const styles = {
  sessionTitle: () => css`
    font-size: 24px;
    margin-bottom: var(--cds-spacing-150);
  `,
  sources: () => css`
    div[role='list']:not(.cds-ShowMoreList-expanded) {
      background-color: var(--cds-color-grey-975);
      background-image: linear-gradient(to bottom, var(--cds-color-grey-975), var(--cds-color-white-0));
      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
    }

    div[role='listitem'] {
      ${typography2.bodyPrimary}
      font-size: 18px;
      line-height: 32px;
      margin-bottom: 0;
      display: flex;

      p {
        ${typography2.bodyPrimary}
        font-size: 18px;
        line-height: 32px;
        margin-bottom: 0;
        display: inline-block;

        a {
          color: #0056d2;
          text-decoration: underline;
        }
      }
    }

    div > .cds-ShowMoreList-ctaButton {
      ${typography2.subtitleMedium}
      color: #0056d2;
      margin-top: 0;
    }
  `,
  singleSources: () => css`
    ${typography2.bodyPrimary}
    color: var(--cds-color-grey-975);
    font-size: 18px;
    padding-left: 0;
    list-style-position: inside;

    p {
      ${typography2.bodyPrimary}
      font-size: 18px;
      line-height: 32px;
      display: inline;

      a {
        color: #0056d2;
        text-decoration: underline;
      }
    }
  `,
};

const logArticlesSourcesEvent = (isExpanded: boolean) => {
  const CONTEXT_DATA = {
    namespace: {
      app: 'articles',
      page: 'article_page',
    },
  };
  const trackingName = isExpanded ? 'article_show_more_sources' : 'article_show_less_sources';
  Retracked.trackComponent(CONTEXT_DATA, { page: window.location.pathname }, trackingName, 'click');
};

const getArticleSourceRichTextOptions = (): Options => {
  return {
    renderNode: {
      ...getDefaultRichTextOptions().renderNode,
      ...getDefaultArticleSourceRenderNodes(logArticlesSourcesEvent),
    },
  };
};

const ArticleSources = ({ body }: Props) => {
  if (!body) return null;

  return (
    <React.Fragment>
      <Typography css={styles.sessionTitle} variant="h1semibold" component="h2">
        {_t('Article sources')}
      </Typography>
      <RichText css={styles.sources} content={body} getRichTextOptions={getArticleSourceRichTextOptions} />
    </React.Fragment>
  );
};

export default ArticleSources;
